import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "FilterTubesAndCylinders",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Filter Components",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Filter Tubes & Cylindrical Screen Assemblies",
      lead: "Engineered from sintered multilayer wire mesh, wedge wire, and precision woven wire cloth for high-differential-pressure and high-temperature liquid and gas filtration systems.",
      secondaryCta: {
        label: "Filter Components Overview",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "Precision welded stainless steel filter tubes and cylinders",
      },
      cardNote: "Calibrated cylindrical geometry & seam welding",
      cardLink: {
        label: "Technical Inquiry",
        to: "/ContactPage",
      },
      specs: [
        { label: "Diameter Range", value: "10 mm – 1,200 mm" },
        { label: "Length Range", value: "100 mm – 3,000 mm" },
        { label: "Retention Cutoff", value: "1 µm – 500 µm" },
        { label: "Alloy Metallurgy", value: "AISI 304, 316L" },
      ],
    },
    overview: {
      eyebrow: "Engineering Architecture",
      heading: "Rigid Cylindrical Filtration Media with Controlled Seam Integrity",
      paragraphs: [
        "Filter tubes and cylindrical filter assemblies provide axial structural stability and continuous circumferential filtration area in industrial process pipelines, autoclave vessels, and hydraulic systems. Manufactured with precision longitudinal TIG or plasma seam welds, each cylinder maintains tight concentricity and true cylindrical roundness across extended service cycles.",
        "Depending on mechanical pressure demands and operational backwash requirements, cylindrical elements are configured as self-supporting multi-layer sintered tubes, perforated core-supported wire cloth sleeves, or heavy-duty wedge wire slot cylinders. This modular structural design permits optimization between fluid flow rate, dirt holding capacity, and mechanical collapse pressure.",
        "End fittings and mounting interfaces are precision-machined from matching stainless steel grades and joined via automated circumferential orbital welding, ensuring leak-free transition into standard housing receptacles or custom manifold assemblies.",
      ],
      keyPoints: [
        "Continuous longitudinal plasma or TIG seam welds calibrated to strict outer diameter and circular runout tolerances",
        "Configurable with inner perforated reinforcement cores, protective outer shroud jackets, or self-supporting multi-layer sintered laminates",
        "Versatile end hardware connections including DOE flat gaskets, SOE with 222/226 O-ring adapters, NPT/BSPT threads, and custom flanges",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "Precision cylindrical filter tubes showing welded end fittings and fine wire mesh media",
      },
    },
    specTable: {
      heading: "Standard Cylindrical Component Specifications",
      intro: "Representative dimensional ranges and structural configurations available across industrial filtration applications. Custom dimensions and tailored end fittings are manufactured to order.",
      columns: [
        { key: "type", label: "Cylinder Configuration", align: "left" },
        { key: "media", label: "Filter Media Type", align: "left" },
        { key: "diameter", label: "Diameter Range (OD)", align: "right" },
        { key: "length", label: "Length Range", align: "right" },
        { key: "retention", label: "Micron Rating", align: "right" },
      ],
      rows: [
        {
          type: "Sintered Porous Tube",
          media: "5-layer sintered mesh laminate",
          diameter: "10 – 200 mm",
          length: "100 – 1,200 mm",
          retention: "1 – 100 µm",
        },
        {
          type: "Reinforced Mesh Sleeve",
          media: "Woven Dutch weave + perforated core",
          diameter: "25 – 450 mm",
          length: "150 – 2,000 mm",
          retention: "5 – 250 µm",
        },
        {
          type: "Square Mesh Strainer Tube",
          media: "Square weave wire cloth + outer guard",
          diameter: "38 – 600 mm",
          length: "200 – 2,500 mm",
          retention: "50 – 500 µm",
        },
        {
          type: "Heavy Wedge Wire Cylinder",
          media: "Profile wire resistance-welded cylinder",
          diameter: "50 – 1,200 mm",
          length: "200 – 3,000 mm",
          retention: "20 – 500 µm",
        },
      ],
      caption: "All dimensions are indicative; custom diameters, lengths, and wall thicknesses are engineered to project specifications.",
    },
    features: {
      heading: "Technical Construction & Manufacturing Capabilities",
      intro: "Every filter tube assembly combines metallurgical precision, rigid geometry, and dependable filtration efficiency across demanding industrial environments.",
      items: [
        {
          title: "Automated Seam Welding",
          text: "High-precision longitudinal plasma and micro-TIG seam welding maintains continuous metallurgical bonding with minimal heat-affected zones, preserving aperture consistency along the weld boundary.",
        },
        {
          title: "Rigid Internal Cores",
          text: "Heavy-gauge perforated or spiral-wound stainless steel internal cores provide resistance against collapse during high differential pressure spikes, reverse flow, and cyclical pump pulsing.",
        },
        {
          title: "Cleanable & Reusable",
          text: "All-stainless metallic construction tolerates routine cleaning via ultrasonic cavitation, backflushing, chemical CIP soaking, and thermal pyrolysis without structural degradation.",
        },
        {
          title: "Machined End Interfaces",
          text: "Precision CNC-turned end caps, threaded couplings, 222/226 dual O-ring adaptors, and custom flanges guarantee positive seating and bypass-free sealing inside standard filter housings.",
        },
      ],
    },
    related: {
      heading: "Related Filter Components & Sibling Assemblies",
      intro: "MutualFilter fabricates an extensive spectrum of custom metal filter components engineered to integrate seamlessly into OEM equipment and industrial filtration lines.",
      cards: [
        {
          title: "Filter Components",
          text: "Explore the comprehensive range of stainless steel discs, tubes, cartridges, screen packs, and custom-formed filtration hardware.",
          to: "/FilterComponentsPage",
          badge: "Parent Category",
          linkLabel: "View Category",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Comprehensive stainless steel filter components and wire mesh assemblies",
          },
        },
        {
          title: "Slit Coils & Stamped Filter Discs",
          text: "Single-layer and sintered multi-layer stamped filter discs, round screens, and narrow-width slit mesh coils with clean, burr-free edges.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          badge: "Sibling Component",
          linkLabel: "Explore Discs",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Stamped stainless steel filter discs and slit wire cloth",
          },
        },
        {
          title: "Filter Cartridges",
          text: "Pleated and cylindrical stainless steel filter cartridge elements engineered for extended surface area, high flow, and rigorous process environments.",
          to: "/FilterCartridgesPage",
          badge: "Sibling Component",
          linkLabel: "Explore Cartridges",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Industrial stainless steel pleated and cylindrical filter cartridges",
          },
        },
        {
          title: "Screen Packs",
          text: "Spot-welded, rimmed, and multi-mesh pack assemblies configured for continuous polymer melt extrusion, blow molding, and resin compounding.",
          to: "/ScreenPacksPage",
          badge: "Sibling Component",
          linkLabel: "Explore Screen Packs",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Spot-welded multi-layer stainless steel screen pack assemblies",
          },
        },
        {
          title: "Filter Baskets and Plates",
          text: "Heavy-duty fabricated strainer baskets, perforated support canisters, and precision flat filter plates for industrial liquid straining.",
          to: "/FilterBasketsAndPlatesPage",
          badge: "Sibling Component",
          linkLabel: "Explore Baskets",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Fabricated stainless steel filter strainer baskets and plates",
          },
        },
      ],
    },
    cta: {
      id: "filter-tubes-quote",
      eyebrow: "Custom Engineering",
      heading: "Request a Custom Filter Tube or Cylindrical Assembly Quote",
      lead: "Provide your target outer diameter, overall length, micron rating, and connection style. Our engineering team prepares technical reviews and detailed proposals for prototype and production runs.",
      secondaryLabel: "Explore Filter Components",
      secondaryTo: "/FilterComponentsPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "FilterTubesAndCylinders",
    relatedTopic: "components",
    hero: {
      categoryBadge: "过滤组件",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "精密滤管与圆筒滤网组件",
      lead: "精选多层烧结金属复合网、楔形绕丝网及精密编织丝网制造，专用于承受高差压与高温工况的工业流体及气体精密过滤系统。",
      secondaryCta: {
        label: "过滤组件总览",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "精密焊接不锈钢滤管与圆筒滤芯组件",
      },
      cardNote: "精密圆筒同轴度与全熔透纵缝焊接",
      cardLink: {
        label: "技术咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "管径范围", value: "10 mm – 1,200 mm" },
        { label: "管长范围", value: "100 mm – 3,000 mm" },
        { label: "过滤精度", value: "1 µm – 500 µm" },
        { label: "材质合金", value: "AISI 304, 316L" },
      ],
    },
    overview: {
      eyebrow: "工程结构设计",
      heading: "具备高结构刚性与受控焊缝精度的圆筒过滤结构",
      paragraphs: [
        "滤管与圆筒过滤组件在管道在线过滤器、反应釜进出口及液压循环系统中提供轴向结构稳定性和连续圆周过滤截面。采用精密微束等离子焊或全自动纵向 TIG 焊接工艺，各圆筒组件在连续工况循环下维持严格的外径公差、圆度及同轴度要求。",
        "根据流体操作压差及反向脉冲清洗要求，滤筒结构可配置为自支撑多层金属烧结网管、冲孔骨架复合包网滤筒或重负荷全焊式楔形丝滤管。这种模块化机械设计有助于在流通截面积、纳污容量与抗崩塌压差之间达到精确平衡。",
        "滤管两端装配接口经精密数控车削加工，通过自动化环缝轨道焊接与筒体牢固连接，确保与工业标准过滤器壳体或定制歧管安装座实现零旁通密封。",
      ],
      keyPoints: [
        "连续微束等离子或 TIG 纵缝全熔透焊接，外径尺寸及圆跳动公差严格受控",
        "可配置冲孔加固内衬骨架、外置保护冲孔套筒或自支撑高强度多层烧结复合网层",
        "适配多元连接端头，包括双开口 DOE 平垫密封、单开口 222/226 双 O 型圈接头、NPT/BSPT 管螺纹及定制法兰",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "精密圆筒滤管展示焊接端头配件与微米级金属网结构",
      },
    },
    specTable: {
      heading: "标准滤管与圆筒组件规格参数",
      intro: "以下列出工业过滤中常见的典型尺寸范围与结构类型。各项规格、端头接口形式及非标长度均可按工程图纸定制生产。",
      columns: [
        { key: "type", label: "结构类型", align: "left" },
        { key: "media", label: "核心过滤介质", align: "left" },
        { key: "diameter", label: "外径范围 (OD)", align: "right" },
        { key: "length", label: "长度范围", align: "right" },
        { key: "retention", label: "过滤精度", align: "right" },
      ],
      rows: [
        {
          type: "多层烧结金属滤管",
          media: "标准 5 层烧结复合网板",
          diameter: "10 – 200 mm",
          length: "100 – 1,200 mm",
          retention: "1 – 100 µm",
        },
        {
          type: "骨架加固复合滤管",
          media: "密纹编织网 + 内置冲孔加固骨架",
          diameter: "25 – 450 mm",
          length: "150 – 2,000 mm",
          retention: "5 – 250 µm",
        },
        {
          type: "方孔过滤套管",
          media: "精密方孔不锈钢网 + 外护冲孔套",
          diameter: "38 – 600 mm",
          length: "200 – 2,500 mm",
          retention: "50 – 500 µm",
        },
        {
          type: "重载楔形绕丝滤管",
          media: "V 型截面型丝电阻熔焊圆筒",
          diameter: "50 – 1,200 mm",
          length: "200 – 3,000 mm",
          retention: "20 – 500 µm",
        },
      ],
      caption: "所列尺寸仅作工程选型参考；可根据项目要求定制特定管径、壁厚与非标总长。",
    },
    features: {
      heading: "制造工艺与技术特点",
      intro: "每件精密滤管组件均结合严谨的冶金选材、精准的机械成型与稳定的过滤阻截性能，适应严苛的工业作业环境。",
      items: [
        {
          title: "全自动纵缝焊接",
          text: "采用高精度数控等离子与微束 TIG 纵缝自动焊接，焊缝熔深均匀，热影响区极小，保证焊缝周边孔径几何结构稳定。",
        },
        {
          title: "坚固内支撑骨架",
          text: "选用厚壁冲孔或螺旋卷焊不锈钢内衬骨架，在高压差冲击、反吹脉冲及流体扰动下提供可靠的抗压塌机械支撑。",
        },
        {
          title: "耐反复清洗再生",
          text: "全金属耐腐蚀材质支持超声波震荡、溶剂化学浸泡、在线高温蒸汽反吹及热解清洗，长期重复使用而不衰减截留性能。",
        },
        {
          title: "数控精密接头加工",
          text: "精密数控加工端盖、管螺纹接头、222/226 双密封圈接头及标准法兰，确保在标准滤筒过滤器中实现严密无泄漏装配。",
        },
      ],
    },
    related: {
      heading: "相关过滤组件与系列产品",
      intro: "MutualFilter 提供完善的定制不锈钢金属过滤组件，便于无缝集成于各类工业装备与流体工艺管网中。",
      cards: [
        {
          title: "过滤组件",
          text: "浏览涵盖不锈钢滤片、滤管、滤芯、组合滤网包及机械冲孔篮框的完整组件系列。",
          to: "/FilterComponentsPage",
          badge: "父级分类",
          linkLabel: "查看分类",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "精密不锈钢过滤组件与丝网装配件",
          },
        },
        {
          title: "分条分切网带与冲压滤片",
          text: "单层及烧结复合冲压过滤网片、圆形网片及边缘整齐无毛刺的精密切割网带产品。",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          badge: "同级产品",
          linkLabel: "探索滤片",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "不锈钢冲压过滤网片与分条丝网带",
          },
        },
        {
          title: "工业滤芯",
          text: "折叠式与筒状不锈钢滤芯元件，具有超大过滤面积与优异的抗压差耐受性能。",
          to: "/FilterCartridgesPage",
          badge: "同级产品",
          linkLabel: "探索滤芯",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "工业不锈钢折叠式与圆筒型滤芯",
          },
        },
        {
          title: "组合滤网包",
          text: "点焊复合与铝边包边多层网包，专用于塑料挤出成型、吹膜及树脂造粒熔体过滤。",
          to: "/ScreenPacksPage",
          badge: "同级产品",
          linkLabel: "探索网包",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "点焊多层不锈钢挤出机过滤网包",
          },
        },
        {
          title: "滤篮与滤板",
          text: "重负荷金属冲孔滤篮、提篮式过滤器内芯及精密不锈钢过滤承托板，用于粗滤与截留保护。",
          to: "/FilterBasketsAndPlatesPage",
          badge: "同级产品",
          linkLabel: "探索滤篮",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "定制不锈钢提篮式过滤网篮与滤板",
          },
        },
      ],
    },
    cta: {
      id: "filter-tubes-quote",
      eyebrow: "定制技术支持",
      heading: "获取精密滤管与圆筒滤网组件报价方案",
      lead: "请提供您的目标外径、总长、过滤精度及端头连接形式，我们的工程团队将为原型试验及批量订单提供快速技术评审与方案建议。",
      secondaryLabel: "返回过滤组件",
      secondaryTo: "/FilterComponentsPage",
      note: "材质报告 (MTR) 与化学成分检测 (PMI) 可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "FilterTubesAndCylinders",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Componentes Filtrantes",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubos Filtrantes y Conjuntos de Cribas Cilíndricas",
      lead: "Fabricados con malla multicapa sinterizada, alambre en cuña y telas metálicas de precisión para sistemas de filtración de líquidos y gases bajo alta presión diferencial y temperatura.",
      secondaryCta: {
        label: "Componentes Filtrantes",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "Tubos y cilindros filtrantes de acero inoxidable soldados de precisión",
      },
      cardNote: "Geometría cilíndrica calibrada y soldadura longitudinal continua",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rango de Diámetro", value: "10 mm – 1.200 mm" },
        { label: "Rango de Longitud", value: "100 mm – 3.000 mm" },
        { label: "Límite de Retención", value: "1 µm – 500 µm" },
        { label: "Metalurgia", value: "AISI 304, 316L" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de Fabricación",
      heading: "Medios Filtrantes Cilíndricos Rígidos con Integridad de Costura Controlada",
      paragraphs: [
        "Los tubos filtrantes y conjuntos cilíndricos garantizan estabilidad estructural axial y un área perimetral continua de filtración en tuberías de proceso, recipientes a presión y circuitos hidráulicos. Soldados longitudinalmente mediante procesos TIG o plasma de alta precisión, cada cilindro mantiene tolerancias estrictas de diámetro exterior y redondez geométrica durante ciclos prolongados.",
        "Dependiendo de los requisitos de presión diferencial y de los ciclos de retrolavado, los elementos cilíndricos se configuran como tubos sinterizados multicapa autoportantes, mallas soportadas por núcleos perforados interiores o tubos de alambre en cuña para servicio pesado. Esta arquitectura modular optimiza el caudal, la capacidad de retención y la resistencia mecánica al colapso.",
        "Los terminales y conexiones de montaje se mecanizan mediante control numérico en aceros inoxidables compatibles y se sueldan orbitalmente al cilindro, asegurando transiciones estancas sin desvío en carcasas estándar o colectores a medida.",
      ],
      keyPoints: [
        "Costuras longitudinales continuas soldadas por TIG o plasma calibradas bajo tolerancias estrictas de diámetro exterior",
        "Configurables con núcleos interiores perforados de refuerzo, camisas exteriores de protección o láminas sinterizadas autoportantes",
        "Conexiones versátiles incluyendo extremos abiertos DOE, terminales SOE 222/226 con juntas tóricas, roscas NPT/BSPT y bridas a medida",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "Tubos cilíndricos filtrantes de acero inoxidable con terminales mecanizados",
      },
    },
    specTable: {
      heading: "Especificaciones Típicas de Tubos Filtrantes",
      intro: "Rangos dimensionales representativos y variantes constructivas habituales en aplicaciones industriales de filtración. Se fabrican longitudes y terminales especiales bajo plano.",
      columns: [
        { key: "type", label: "Configuración Cilíndrica", align: "left" },
        { key: "media", label: "Medio Filtrante", align: "left" },
        { key: "diameter", label: "Diámetro Exterior (OD)", align: "right" },
        { key: "length", label: "Longitud", align: "right" },
        { key: "retention", label: "Grado de Filtración", align: "right" },
      ],
      rows: [
        {
          type: "Tubo Sinterizado Poroso",
          media: "Laminado sinterizado de 5 capas",
          diameter: "10 – 200 mm",
          length: "100 – 1.200 mm",
          retention: "1 – 100 µm",
        },
        {
          type: "Cilindro con Núcleo Perforado",
          media: "Tela holandesa + núcleo interior perforado",
          diameter: "25 – 450 mm",
          length: "150 – 2.000 mm",
          retention: "5 – 250 µm",
        },
        {
          type: "Tubo Colador de Malla Cuadrada",
          media: "Malla cuadrada + camisa protectora exterior",
          diameter: "38 – 600 mm",
          length: "200 – 2.500 mm",
          retention: "50 – 500 µm",
        },
        {
          type: "Cilindro de Alambre en Cuña",
          media: "Alambre de perfil electrosoldado",
          diameter: "50 – 1.200 mm",
          length: "200 – 3.000 mm",
          retention: "20 – 500 µm",
        },
      ],
      caption: "Las dimensiones son indicativas; es posible fabricar diámetros, espesores y longitudes a medida según especificaciones del proyecto.",
    },
    features: {
      heading: "Capacidades de Fabricación y Ventajas Técnicas",
      intro: "Cada conjunto cilíndrico integra selección metalúrgica controlada, rigidez estructural y retención precisa en entornos industriales exigentes.",
      items: [
        {
          title: "Soldadura Longitudinal Automatizada",
          text: "Soldadura por micro-TIG o plasma de alta precisión que logra unión metalúrgica continua con una zona afectada por calor reducida, protegiendo la homogeneidad de los poros.",
        },
        {
          title: "Núcleos Interiores Rígidos",
          text: "Tubos interiores perforados o enrollados en espiral de acero inoxidable que proporcionan alta resistencia contra colapso mecánico ante sobrepresiones e inversiones de flujo.",
        },
        {
          title: "Lavable y Reutilizable",
          text: "Estructura íntegramente metálica apta para limpieza ultrasónica repetida, retrolavado a contracorriente, disolventes químicos y ciclos térmicos sin deterioro.",
        },
        {
          title: "Terminales Mecanizados CNC",
          text: "Casquillos, acoplamientos roscados, adaptadores de doble junta 222/226 y bridas torneados por CNC que garantizan un sellado positivo sin fugas en la carcasa receptora.",
        },
      ],
    },
    related: {
      heading: "Componentes Filtrantes Relacionados",
      intro: "MutualFilter suministra una amplia gama de componentes metálicos a medida para fabricantes de equipos OEM y plantas de proceso.",
      cards: [
        {
          title: "Componentes Filtrantes",
          text: "Consulte la gama completa de discos, tubos, cartuchos, paquetes de malla y cestas de filtración en acero inoxidable.",
          to: "/FilterComponentsPage",
          badge: "Categoría Principal",
          linkLabel: "Ver Categoría",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Componentes filtrantes de acero inoxidable de precisión",
          },
        },
        {
          title: "Bobinas Cortadas y Discos Filtrantes",
          text: "Discos filtrantes estampados en una o múltiples capas y cintas cortadas a medida con bordes limpios y libres de rebabas.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          badge: "Componente Afín",
          linkLabel: "Explorar Discos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Discos filtrantes estampados de acero inoxidable",
          },
        },
        {
          title: "Cartuchos Filtrantes",
          text: "Cartuchos metálicos plisados y cilíndricos diseñados para gran superficie de filtración y condiciones térmicas severas.",
          to: "/FilterCartridgesPage",
          badge: "Componente Afín",
          linkLabel: "Explorar Cartuchos",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Cartuchos filtrantes industriales de acero inoxidable",
          },
        },
        {
          title: "Paquetes de Malla",
          text: "Conjuntos de mallas soldados por puntos o bordeados con perfiles de aluminio para extrusión de polímeros y reciclaje.",
          to: "/ScreenPacksPage",
          badge: "Componente Afín",
          linkLabel: "Explorar Paquetes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Paquetes de mallas filtrantes soldados por puntos",
          },
        },
        {
          title: "Cestas y Placas Filtrantes",
          text: "Cestas coladoras de alta resistencia mecánica, cuerpos perforados y placas soporte planas para retención de sólidos en líquidos.",
          to: "/FilterBasketsAndPlatesPage",
          badge: "Componente Afín",
          linkLabel: "Explorar Cestas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Cestas filtrantes y placas de soporte de acero inoxidable",
          },
        },
      ],
    },
    cta: {
      id: "filter-tubes-quote",
      eyebrow: "Fabricación a Medida",
      heading: "Solicite Cotización para Tubos Filtrantes y Cilindros Metálicos",
      lead: "Indique diámetro exterior, longitud total, micraje requerido y tipo de conexión. Nuestro departamento técnico revisará sus requisitos para cotizaciones prototipo y de serie.",
      secondaryLabel: "Ver Componentes Filtrantes",
      secondaryTo: "/FilterComponentsPage",
      note: "Informes de ensayo de material (MTR) y análisis PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "FilterTubesAndCylinders",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Filterkomponenten",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Filterrohre & Zylindrische Filterbaugruppen",
      lead: "Gefertigt aus mehrlagigem Sintergewebe, Spaltsieben und Drahtgewebe für anspruchsvolle Flüssigkeits- und Gasfiltrationsprozesse unter hoher Druckdifferenz und Temperatur.",
      secondaryCta: {
        label: "Übersicht Filterkomponenten",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "Präzisionsgeschweißte Edelstahl-Filterrohre und -zylinder",
      },
      cardNote: "Kalibrierte Zylindergeometrie & Längsnahtschweißung",
      cardLink: {
        label: "Technische Beratung",
        to: "/ContactPage",
      },
      specs: [
        { label: "Durchmesserbereich", value: "10 mm – 1.200 mm" },
        { label: "Längenbereich", value: "100 mm – 3.000 mm" },
        { label: "Filterfeinheit", value: "1 µm – 500 µm" },
        { label: "Werkstoffe", value: "AISI 304, 316L" },
      ],
    },
    overview: {
      eyebrow: "Konstruktiver Aufbau",
      heading: "Formstabile Zylinder-Filterkörper mit definierter Schweißnahtgüte",
      paragraphs: [
        "Filterrohre und zylindrische Filterelemente sichern axiale Formstabilität und eine unterbrechungsfreie Umfangsfilterfläche in Rohrleitungen, Druckbehältern und hydraulischen Anlagen. Präzise WIG- oder Plasma-Längsnahtschweißungen gewährleisten enge Außendurchmessertoleranzen und minimale Rundlaufabweichungen über lange Standzeiten.",
        "Je nach Differenzdruckbeanspruchung und Rückspülanforderungen werden die Zylinderelemente als selbsttragende mehrlagige Sinterrohre, drahtgewebeummantelte Lochblechkerne oder robuste Spaltsiebzylinder ausgeführt. Dieser modulare Aufbau ermöglicht eine zielgerichtete Abstimmung von Durchflussleistung, Schmutzaufnahmekapazität und Berstdruckfestigkeit.",
        "Endstücke und Anschlussadapter werden aus abgestimmten Edelstahlqualitäten CNC-gefertigt und mittels automatisierter Orbitalschweißung mit dem Filterkörper gefügt, um eine verlässliche, bypassfreie Abdichtung im Filtergehäuse zu gewährleisten.",
      ],
      keyPoints: [
        "Kontinuierliche WIG- oder Plasma-Längsnähte, kalibriert nach engen Durchmesser- und Rundlauftoleranzen",
        "Kombinierbar mit Lochblech-Innenstützkörpern, äußeren Schutzmänteln oder als selbsttragende Sintermetall-Verbundrohre",
        "Vielseitige Anschlussgeometrien wie DOE-Flachdichtung, SOE mit 222/226 O-Ringen, Gewindenippel sowie kundenspezifische Flansche",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "Zylindrische Edelstahl-Filterrohre mit Endanschlüssen und feinem Drahtgewebe",
      },
    },
    specTable: {
      heading: "Technische Parameter Typischer Filterrohre",
      intro: "Repräsentative Abmessungsbereiche und Bauformen für industrielle Prozessfiltrationsanlagen. Sondermaße und anwendungsspezifische Anschlüsse fertigen wir nach Kundenzeichnung.",
      columns: [
        { key: "type", label: "Zylinderbauform", align: "left" },
        { key: "media", label: "Filtermedium", align: "left" },
        { key: "diameter", label: "Außendurchmesser (OD)", align: "right" },
        { key: "length", label: "Länge", align: "right" },
        { key: "retention", label: "Filterfeinheit", align: "right" },
      ],
      rows: [
        {
          type: "Poröses Sinterfilterrohr",
          media: "5-lagiges Sintergewebelaminat",
          diameter: "10 – 200 mm",
          length: "100 – 1.200 mm",
          retention: "1 – 100 µm",
        },
        {
          type: "Stützkörper-Geweberohr",
          media: "Tressengewebe + Lochblechkern",
          diameter: "25 – 450 mm",
          length: "150 – 2.000 mm",
          retention: "5 – 250 µm",
        },
        {
          type: "Quadratmaschen-Siebrohr",
          media: "Drahtgewebe + äußerer Schutzmantel",
          diameter: "38 – 600 mm",
          length: "200 – 2.500 mm",
          retention: "50 – 500 µm",
        },
        {
          type: "Spaltsieb-Filterrohr",
          media: "Widerstandsgeschweißtes Profilrohr",
          diameter: "50 – 1.200 mm",
          length: "200 – 3.000 mm",
          retention: "20 – 500 µm",
        },
      ],
      caption: "Alle Abmessungen dienen als Richtwerte; projektspezifische Durchmesser, Längen und Wandstärken werden auftragsbezogen gefertigt.",
    },
    features: {
      heading: "Fertigungskompetenz & Technische Merkmale",
      intro: "Jede Filterrohrbaugruppe vereint werkstoffliche Präzision, geometrische Stabilität und reproduzierbare Trennleistung für anspruchsvolle Betriebsumgebungen.",
      items: [
        {
          title: "Automatisierte Längsnahtschweißung",
          text: "Mikro-WIG- und Plasmaschweißungen mit kontinuierlicher Durchschweißung und minimaler Wärmeeinflusszone halten das Maschengefüge bis an die Nahtkante intakt.",
        },
        {
          title: "Formstabile Stützkörper",
          text: "Starke Lochblech- oder Spiralstützkörper aus Edelstahl widerstehen mechanischen Druckspitzen, Fließrichtungswechseln und Pumpenschwingungen ohne Verformung.",
        },
        {
          title: "Reinigung & Mehrfachverwendung",
          text: "Die korrosionsbeständige Ganzmetallkonstruktion erlaubt wiederholte Ultraschallreinigung, Rückspülung, chemische Spülbäder und thermische Zersetzung.",
        },
        {
          title: "CNC-gedrehte Anschlüsse",
          text: "Präzisionsanschlüsse, Gewindenippel, 222/226 O-Ring-Adapter und Flansche stellen einen formschlüssigen, leckagefreien Sitz im Filtergehäuse sicher.",
        },
      ],
    },
    related: {
      heading: "Verwandte Filterkomponenten & Baugruppen",
      intro: "MutualFilter fertigt ein breites Spektrum maßgeschneiderter Metallfilterkomponenten für OEM-Anlagenbauer und Prozessindustrien.",
      cards: [
        {
          title: "Filterkomponenten",
          text: "Entdecken Sie unser gesamtes Programm an Filterscheiben, Rohren, Kerzen, Siebpaketen und Siebkörben aus Edelstahl.",
          to: "/FilterComponentsPage",
          badge: "Hauptkategorie",
          linkLabel: "Kategorie ansehen",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Edelstahl-Filterkomponenten und Drahtgewebebaugruppen",
          },
        },
        {
          title: "Spaltbänder & Filterscheiben",
          text: "Einlagige und mehrlagig gesinterte gestanzte Filterscheiben und schmale Gewebebänder mit sauberen, gratfreien Schnittkanten.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          badge: "Geschwisterprodukt",
          linkLabel: "Scheiben entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Gestanzte Filterscheiben und Gewebebänder aus Edelstahl",
          },
        },
        {
          title: "Filterkerzen",
          text: "Plissierte und zylindrische Edelstahl-Filterkerzen für vergrößerte Filterflächen und anspruchsvolle thermische Prozessbedingungen.",
          to: "/FilterCartridgesPage",
          badge: "Geschwisterprodukt",
          linkLabel: "Filterkerzen entdecken",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Industrielle plissierte und zylindrische Filterkerzen",
          },
        },
        {
          title: "Siebpakete",
          text: "Punktgeschweißte und eingefasste Mehrlagen-Siebpakete für Polymer-Schmelzefiltration, Blasformen und Kunststoffrecycling.",
          to: "/ScreenPacksPage",
          badge: "Geschwisterprodukt",
          linkLabel: "Siebpakete entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Punktgeschweißte mehrlagige Siebpakete für Extrusionsanlagen",
          },
        },
        {
          title: "Filterkörbe & Filterplatten",
          text: "Robuste Siebkörbe, Lochblecheinsätze und ebene Trägerplatten für die Grobfiltration und den Schutz von Rohrleitungspumpen.",
          to: "/FilterBasketsAndPlatesPage",
          badge: "Geschwisterprodukt",
          linkLabel: "Körbe entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Robuste Siebkörbe und Filterplatten aus Edelstahl",
          },
        },
      ],
    },
    cta: {
      id: "filter-tubes-quote",
      eyebrow: "Sonderanfertigung",
      heading: "Angebot für Filterrohre & Zylinder-Filterelemente anfragen",
      lead: "Nennen Sie Außendurchmesser, Länge, Filterfeinheit und Anschlussart. Unser technisches Team prüft Ihre Spezifikationen und unterbreitet ein detailliertes Angebot.",
      secondaryLabel: "Zurück zu Filterkomponenten",
      secondaryTo: "/FilterComponentsPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Anfrage gemäß Auftragsvereinbarung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "FilterTubesAndCylinders",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Composants Filtrants",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubes Filtrants et Crépines Cylindriques",
      lead: "Fabriqués en toile métallique frittée multicouche, fil profilé en cuisse et toiles tissées pour les systèmes de filtration de fluides et de gaz sous haute pression et température.",
      secondaryCta: {
        label: "Aperçu des Composants",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "Tubes et cylindres filtrants soudés de précision en acier inoxydable",
      },
      cardNote: "Géométrie cylindrique rigide et soudure longitudinale continue",
      cardLink: {
        label: "Assistance Technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Plage de Diamètres", value: "10 mm – 1 200 mm" },
        { label: "Plage de Longueurs", value: "100 mm – 3 000 mm" },
        { label: "Seuil de Coupure", value: "1 µm – 500 µm" },
        { label: "Nuances d'Acier", value: "AISI 304, 316L" },
      ],
    },
    overview: {
      eyebrow: "Conception Technique",
      heading: "Médias Filtrants Cylindriques Rigides à Soudure Maîtrisée",
      paragraphs: [
        "Les tubes filtrants et assemblages cylindriques confèrent une stabilité axiale élevée et une surface continue de filtration dans les lignes de tuyauterie, les autoclaves et les systèmes hydrauliques. Réalisés par soudage longitudinal TIG ou micro-plasma automatisé, chaque cylindre garantit une concentricité et une circularité rigoureuses au cours de cycles prolongés.",
        "Selon les contraintes de pression différentielle et les exigences de rétrolavage, les cylindres sont configurés en tubes frittés multicouches autoportants, toiles supportées par un noyau perforé interne ou cylindres en fil profilé pour fortes charges. Cette structure modulaire optimise le débit volumique, la capacité de rétention et la résistance à l'écrasement.",
        "Les embouts et raccords sont usinés sur tours à commande numérique dans des nuances d'acier inoxydable compatibles et soudés par procédé orbital, assurant une étanchéité sans dérivation dans les corps de filtre standards.",
      ],
      keyPoints: [
        "Soudures longitudinales continues TIG ou plasma calibrées selon des tolérances précises de diamètre extérieur",
        "Configurables avec noyaux internes perforés de renfort, jaquettes extérieures protectrices ou toiles frittées autoportantes",
        "Raccordements polyvalents : embouts plats DOE, embouts SOE 222/226 à double joint torique, filetages NPT/BSPT et brides spécifiques",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "Tubes filtrants cylindriques en acier inoxydable avec raccords usinés",
      },
    },
    specTable: {
      heading: "Spécifications des Tubes et Éléments Cylindriques",
      intro: "Plages dimensionnelles représentatives et variantes de fabrication courantes dans les procédés industriels. Fabrications spéciales et embouts sur mesure réalisés selon plans.",
      columns: [
        { key: "type", label: "Type de Cylindre", align: "left" },
        { key: "media", label: "Média Filtrant", align: "left" },
        { key: "diameter", label: "Diamètre Extérieur (OD)", align: "right" },
        { key: "length", label: "Longueur", align: "right" },
        { key: "retention", label: "Seuil de Filtration", align: "right" },
      ],
      rows: [
        {
          type: "Tube Fritté Poreux",
          media: "Stratifié fritté standard 5 couches",
          diameter: "10 – 200 mm",
          length: "100 – 1 200 mm",
          retention: "1 – 100 µm",
        },
        {
          type: "Cylindre avec Support Perforé",
          media: "Toile hollandaise + tube interne perforé",
          diameter: "25 – 450 mm",
          length: "150 – 2 000 mm",
          retention: "5 – 250 µm",
        },
        {
          type: "Tube Tamis Maille Carrée",
          media: "Toile carrée + enveloppe de protection",
          diameter: "38 – 600 mm",
          length: "200 – 2 500 mm",
          retention: "50 – 500 µm",
        },
        {
          type: "Cylindre en Fil Profilé",
          media: "Fil profilé en V électrosoudé",
          diameter: "50 – 1 200 mm",
          length: "200 – 3 000 mm",
          retention: "20 – 500 µm",
        },
      ],
      caption: "Dimensions indicatives ; diamètres, épaisseurs et longueurs personnalisés selon les exigences de votre cahier des charges.",
    },
    features: {
      heading: "Savoir-faire et Caractéristiques Techniques",
      intro: "Chaque tube filtrant associe rigueur métallurgique, géométrie stable et efficacité de séparation reproductible pour les environnements de process sévères.",
      items: [
        {
          title: "Soudage Longitudinal Automatisé",
          text: "Procédé de soudage plasma et micro-TIG assurant une pénétration continue et une zone affectée thermiquement très restreinte, préservant la régularité des mailles.",
        },
        {
          title: "Noyaux Internes Renforcés",
          text: "Tubes internes perforés en tôle forte ou roulés en spirale garantissant une résistance mécanique élevée face aux coups de bélier et inversions de flux.",
        },
        {
          title: "Nettoyage et Réutilisation",
          text: "Construction métallique résistant aux nettoyages par ultrasons, aux contre-lavages répétés, aux bains chimiques et aux régénérations thermiques.",
        },
        {
          title: "Embouts Usinés CNC",
          text: "Raccords filetés, embouts à double joint torique 222/226 et brides usinés sur tours CNC pour un montage sans fuite ni dérivation dans les carters.",
        },
      ],
    },
    related: {
      heading: "Composants Filtrants Associés",
      intro: "MutualFilter conçoit une gamme complète de composants métalliques adaptés aux exigences des constructeurs d'équipements OEM et des usines de procédés.",
      cards: [
        {
          title: "Composants Filtrants",
          text: "Découvrez notre sélection complète de disques, tubes, cartouches, packs de toiles et paniers filtrants en acier inoxydable.",
          to: "/FilterComponentsPage",
          badge: "Catégorie Parente",
          linkLabel: "Voir la catégorie",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Composants filtrants de précision en acier inoxydable",
          },
        },
        {
          title: "Bobines et Disques Filtrants",
          text: "Disques découpés simples ou multicouches frittés et bandes refendues précises à bords nets sans bavures.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          badge: "Produit Associé",
          linkLabel: "Découvrir les disques",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Disques filtrants estampés et toiles métalliques refendues",
          },
        },
        {
          title: "Cartouches Filtrantes",
          text: "Éléments filtrants plissés et cylindriques en inox offrant une surface de filtration accrue et une excellente résistance thermique.",
          to: "/FilterCartridgesPage",
          badge: "Produit Associé",
          linkLabel: "Découvrir les cartouches",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Cartouches filtrantes industrielles en acier inoxydable",
          },
        },
        {
          title: "Packs de Toiles Filtrantes",
          text: "Ensembles soudés par points ou sertis sur cadre aluminium pour l'extrusion de polymères et le recyclage des résines.",
          to: "/ScreenPacksPage",
          badge: "Produit Associé",
          linkLabel: "Découvrir les packs",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Packs de toiles soudés par points pour extrudeuses",
          },
        },
        {
          title: "Paniers et Plaques Filtrantes",
          text: "Paniers filtrants en tôle perforée, éléments amovibles et plaques supports rigides pour la rétention de particules solides.",
          to: "/FilterBasketsAndPlatesPage",
          badge: "Produit Associé",
          linkLabel: "Découvrir les paniers",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Paniers filtrants et plaques perforées en acier inoxydable",
          },
        },
      ],
    },
    cta: {
      id: "filter-tubes-quote",
      eyebrow: "Fabrication sur Mesure",
      heading: "Demandez un Devis pour Tubes et Crépines Cylindriques",
      lead: "Indiquez votre diamètre extérieur, la longueur totale, la finesse de filtration et le type de raccordement. Notre équipe technique examinera rapidement votre projet.",
      secondaryLabel: "Retour aux Composants",
      secondaryTo: "/FilterComponentsPage",
      note: "Certificats de contrôle matière (MTR) et contrôles PMI disponibles sur demande selon spécifications de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "FilterTubesAndCylinders",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Componentes Filtrantes",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubos Filtrantes e Cilindros de Tela",
      lead: "Projetados a partir de malha sinterizada multicamadas, arame em cunha e telas tecidas para sistemas de filtração de líquidos e gases sob alta pressão diferencial e temperatura.",
      secondaryCta: {
        label: "Visão Geral de Componentes",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "Tubos e cilindros filtrantes em aço inoxidável soldados com precisão",
      },
      cardNote: "Geometria cilíndrica calibrada e solda longitudinal contínua",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Faixa de Diâmetro", value: "10 mm – 1.200 mm" },
        { label: "Faixa de Comprimento", value: "100 mm – 3.000 mm" },
        { label: "Retenção Nominal", value: "1 µm – 500 µm" },
        { label: "Ligas Metálicas", value: "AISI 304, 316L" },
      ],
    },
    overview: {
      eyebrow: "Engenharia Estrutural",
      heading: "Elementos Filtrantes Cilíndricos com Integridade de Solda Controlada",
      paragraphs: [
        "Tubos filtrantes e conjuntos cilíndricos oferecem estabilidade estrutural axial e área de passagem contínua em linhas de tubulação de processo, vasos de pressão e circuitos hidráulicos. Soldados longitudinalmente por TIG ou microplasma de precisão, cada tubo mantém tolerâncias rígidas de diâmetro externo e circularidade durante longos ciclos de operação.",
        "Dependendo dos requisitos de pressão diferencial e das rotinas de retrolavagem, os elementos são configurados como tubos sinterizados multicamadas autoportantes, mangas de tela com núcleo interno perfurado ou tubos de arame em cunha para serviço pesado. Essa arquitetura modular equilibra vazão, capacidade de retenção de particulados e resistência ao colapso.",
        "Conexões de extremidade e adaptadores são usinados em centro CNC a partir de ligas compatíveis e soldados circunferencialmente por processo orbital, garantindo vedação positiva e livre de desvios em carcaças industriais padronizadas.",
      ],
      keyPoints: [
        "Soldas longitudinais contínuas por TIG ou plasma calibradas sob rigorosas tolerâncias de diâmetro e circularidade",
        "Opções com tubos internos perfurados de apoio, camisas externas de proteção ou estruturas sinterizadas autoportantes",
        "Terminais de conexão flexíveis: DOE com vedação plana, SOE 222/226 com anéis O-ring, roscas NPT/BSPT e flanges customizadas",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
        alt: "Tubos cilíndricos filtrantes em aço inoxidável com terminais usinados",
      },
    },
    specTable: {
      heading: "Especificações Típicas de Tubos Filtrantes",
      intro: "Dimensões representativas e variantes construtivas aplicadas em sistemas de filtração industrial. Dimensões especiais e conexões sob medida são produzidas conforme projeto.",
      columns: [
        { key: "type", label: "Configuração do Tubo", align: "left" },
        { key: "media", label: "Meio Filtrante", align: "left" },
        { key: "diameter", label: "Diâmetro Externo (OD)", align: "right" },
        { key: "length", label: "Comprimento", align: "right" },
        { key: "retention", label: "Grau de Retenção", align: "right" },
      ],
      rows: [
        {
          type: "Tubo Sinterizado Poroso",
          media: "Laminado sinterizado de 5 camadas",
          diameter: "10 – 200 mm",
          length: "100 – 1.200 mm",
          retention: "1 – 100 µm",
        },
        {
          type: "Tubo com Núcleo Perfurado",
          media: "Tela holandesa + tubo perfurado interno",
          diameter: "25 – 450 mm",
          length: "150 – 2.000 mm",
          retention: "5 – 250 µm",
        },
        {
          type: "Tubo de Malha Quadrada",
          media: "Tela de malha quadrada + camisa externa",
          diameter: "38 – 600 mm",
          length: "200 – 2.500 mm",
          retention: "50 – 500 µm",
        },
        {
          type: "Tubo de Arame em Cunha",
          media: "Arame perfilado soldado por resistência",
          diameter: "50 – 1.200 mm",
          length: "200 – 3.000 mm",
          retention: "20 – 500 µm",
        },
      ],
      caption: "Todas as dimensões são orientativas; diâmetros, comprimentos e espessuras específicas são fabricados sob encomenda.",
    },
    features: {
      heading: "Capacidades de Fabricação e Destaques Técnicos",
      intro: "Cada tubo filtrante combina rigor metalúrgico, precisão geométrica e confiabilidade de retenção em ambientes industriais exigentes.",
      items: [
        {
          title: "Soldagem Longitudinal Automatizada",
          text: "Soldas micro-TIG e plasma de penetração completa com zona termicamente afetada reduzida, mantendo a integridade dimensional das aberturas da malha.",
        },
        {
          title: "Núcleos de Suporte Rígidos",
          text: "Tubos internos perfurados ou helicoidais em aço inoxidável espesso que garantem suporte contra colapso sob picos de pressão e refluxo.",
        },
        {
          title: "Lavável e Reutilizável",
          text: "Construção metálica resistente à cavitação ultrassônica, retrolavagem vigorosa, imersão em solventes químicos e ciclos de pirólise.",
        },
        {
          title: "Conexões Usinadas CNC",
          text: "Terminais usinados em torno CNC com roscas, adaptadores de duplo O-ring 222/226 e flanges para vedação confiável e livre de vazamento nas carcaças.",
        },
      ],
    },
    related: {
      heading: "Componentes Filtrantes Relacionados",
      intro: "A MutualFilter projeta e produz componentes metálicos customizados para fabricantes de equipamentos OEM e plantas de processo.",
      cards: [
        {
          title: "Componentes Filtrantes",
          text: "Veja a linha abrangente de discos, tubos, cartuchos, pacotes de telas e cestos filtrantes em aço inoxidável.",
          to: "/FilterComponentsPage",
          badge: "Categoria Principal",
          linkLabel: "Ver Categoria",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Componentes filtrantes de precisão em aço inoxidável",
          },
        },
        {
          title: "Bobinas Cortadas e Discos Filtrantes",
          text: "Discos filtrantes estampados simples ou sinterizados e bobinas estreitas cortadas com bordas limpas e sem rebarbas.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          badge: "Produto Irmão",
          linkLabel: "Explorar Discos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Discos filtrantes estampados de aço inoxidável",
          },
        },
        {
          title: "Cartuchos Filtrantes",
          text: "Cartuchos plissados e cilíndricos em aço inoxidável para ampla área de filtração e elevadas temperaturas de operação.",
          to: "/FilterCartridgesPage",
          badge: "Produto Irmão",
          linkLabel: "Explorar Cartuchos",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Cartuchos filtrantes industriais em aço inoxidável",
          },
        },
        {
          title: "Pacotes de Telas",
          text: "Conjuntos de telas soldados por ponto ou emoldurados em alumínio para extrusão de polímeros e reciclagem de resinas.",
          to: "/ScreenPacksPage",
          badge: "Produto Irmão",
          linkLabel: "Explorar Pacotes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Pacotes de telas em aço inoxidável soldados por ponto",
          },
        },
        {
          title: "Cestos e Placas Filtrantes",
          text: "Cestos filtrantes em chapa perfurada, cestos tipo cesto e placas suporte planas para retenção de sólidos em líquidos.",
          to: "/FilterBasketsAndPlatesPage",
          badge: "Produto Irmão",
          linkLabel: "Explorar Cestos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Cestos e placas filtrantes em aço inoxidável",
          },
        },
      ],
    },
    cta: {
      id: "filter-tubes-quote",
      eyebrow: "Engenharia Sob Medida",
      heading: "Solicite uma Cotação para Tubos e Cilindros Filtrantes",
      lead: "Informe o diâmetro externo, comprimento total, grau de retenção e padrão de conexão. Nossa equipe técnica formulará propostas para protótipos e produção seriada.",
      secondaryLabel: "Voltar a Componentes",
      secondaryTo: "/FilterComponentsPage",
      note: "Relatórios de ensaio de material (MTR) e análise PMI disponíveis quando especificados no pedido.",
    },
  },
};
